import { Loader2, MessageSquare, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAddComment, useDeleteComment, useItem } from "@/hooks/use-items";
import { formatDate } from "@/lib/format";

interface ItemCommentsProps {
  itemId: string;
}

export function ItemComments({ itemId }: ItemCommentsProps) {
  const { data: item, isLoading, isError, error, refetch } = useItem(itemId);
  const comments = item?.comments ?? [];
  const [content, setContent] = useState("");
  const [deleteComment, setDeleteComment] = useState<{ id: string; preview: string } | null>(null);

  const addComment = useAddComment(itemId);
  const removeComment = useDeleteComment(itemId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    addComment.mutate(content.trim(), {
      onSuccess: () => setContent(""),
    });
  }

  function handleDeleteConfirm() {
    if (!deleteComment) return;
    removeComment.mutate(deleteComment.id, {
      onSuccess: () => setDeleteComment(null),
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load comments"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">Comments ({comments.length})</h3>
      </div>

      {/* Comment list */}
      {comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No comments yet.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="rounded-lg border border-border bg-muted/30 p-3 space-y-1"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{comment.authorName}</span>
                  <span className="text-muted-foreground">{formatDate(comment.createdAt)}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  onClick={() =>
                    setDeleteComment({
                      id: comment.id,
                      preview: comment.content.slice(0, 50),
                    })
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="text-sm">{comment.content}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add comment form */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a comment..."
          className="min-h-[60px] flex-1 resize-none"
        />
        <Button type="submit" size="sm" disabled={addComment.isPending || !content.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </form>

      <ConfirmDialog
        open={deleteComment !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteComment(null);
        }}
        title="Delete comment"
        description="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
